import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";

import {
  buildCoursesCalendarBulkProduceJoinDto,
  DrawerType,
  IDrawerCoursesCalendarRideProduceProps,
  IInjectedDrawerProps,
  Log,
  simpleResultTrue,
  useDrawers,
} from "@ryde/shared";

import {
  getCoursesCalendarProducePreviewHintKey,
  isCoursesCalendarProduceSlotSkipped,
  toProduceDrawerError,
} from "./parts";

export type IUseDrawerCoursesCalendarRideProduceParams =
  IDrawerCoursesCalendarRideProduceProps & IInjectedDrawerProps;

const collectReadySlotIds = (
  slots: IDrawerCoursesCalendarRideProduceProps["slots"],
): Set<string> =>
  new Set(
    slots
      .filter(slot => !isCoursesCalendarProduceSlotSkipped(slot))
      .map(slot => slot.id),
  );

export const useDrawerCoursesCalendarRideProduce = ({
  slots,
  createSlotRide,
  shouldUseSharedProduceFlow = false,
  onAfterChange,
  onCloseDrawer,
  resolveFn,
}: IUseDrawerCoursesCalendarRideProduceParams) => {
  const { t } = useTranslation();
  const { openAsyncDrawer } = useDrawers();
  const [isWorking, setIsWorking] = useState(false);
  const [progress, setProgress] = useState(0);
  const [selectedSlotIds, setSelectedSlotIds] = useState<Set<string>>(() =>
    collectReadySlotIds(slots),
  );

  useEffect(() => {
    setSelectedSlotIds(collectReadySlotIds(slots));
  }, [slots]);

  const slotsWithoutRide = useMemo(
    () => slots.filter(slot => !isCoursesCalendarProduceSlotSkipped(slot)),
    [slots],
  );

  const selectedSlotsToProduce = useMemo(
    () =>
      slots.filter(
        slot =>
          selectedSlotIds.has(slot.id) &&
          !isCoursesCalendarProduceSlotSkipped(slot),
      ),
    [selectedSlotIds, slots],
  );

  const producePreviewHintKey = useMemo(
    () =>
      getCoursesCalendarProducePreviewHintKey({
        slotsCount: slots.length,
        slotsWithoutRideCount: selectedSlotsToProduce.length,
      }),
    [selectedSlotsToProduce.length, slots.length],
  );

  const isSaveDisabled = isWorking || !selectedSlotsToProduce.length;
  const showSequentialProgress = isWorking && !shouldUseSharedProduceFlow;

  const toggleSlotSelected = useEvent((slotId: string): void => {
    const slot = slots.find(item => item.id === slotId);
    if (!slot || isCoursesCalendarProduceSlotSkipped(slot)) {
      return;
    }

    setSelectedSlotIds(prev => {
      const next = new Set(prev);
      if (next.has(slotId)) {
        next.delete(slotId);
      } else {
        next.add(slotId);
      }
      return next;
    });
  });

  const selectAllReadySlots = useEvent((): void => {
    setSelectedSlotIds(collectReadySlotIds(slots));
  });

  const deselectAllReadySlots = useEvent((): void => {
    setSelectedSlotIds(new Set());
  });

  const runSharedProduceFlow = useEvent(async (): Promise<void> => {
    const produceJoin = buildCoursesCalendarBulkProduceJoinDto(
      selectedSlotsToProduce,
    );
    onCloseDrawer();
    await openAsyncDrawer({
      drawerProps: {
        type: DrawerType.ProductionPreview,
        ...produceJoin,
        isRedirectToProducedRidesAfterProductionFinish: true,
        isCloseAfterProductionFinish: false,
      },
    });
    onAfterChange?.();
    resolveFn?.(simpleResultTrue);
  });

  const runSequentialProduceFlow = useEvent(async (): Promise<void> => {
    if (!createSlotRide) {
      throw new Error("createSlotRide is required for sequential produce");
    }

    setProgress(0);
    let done = 0;
    for (const slot of selectedSlotsToProduce) {
      await createSlotRide(slot);
      done += 1;
      setProgress(Math.round((100 * done) / selectedSlotsToProduce.length));
    }
    onAfterChange?.();
    resolveFn?.(simpleResultTrue);
    onCloseDrawer();
  });

  const onProduceClick = useEvent(async (): Promise<void> => {
    if (!selectedSlotsToProduce.length) {
      return;
    }

    setIsWorking(true);
    try {
      if (shouldUseSharedProduceFlow) {
        await runSharedProduceFlow();
      } else {
        await runSequentialProduceFlow();
      }
    } catch (error: unknown) {
      Log.errorAndMessageToast({
        message: t("course.calendar.producePreviewError"),
        error: toProduceDrawerError(error),
      });
    } finally {
      setIsWorking(false);
    }
  });

  return {
    slots,
    slotsWithoutRide,
    selectedSlotIds,
    selectedSlotsToProduce,
    selectedCount: selectedSlotsToProduce.length,
    isWorking,
    progress,
    producePreviewHintKey,
    isSaveDisabled,
    showSequentialProgress,
    toggleSlotSelected,
    selectAllReadySlots,
    deselectAllReadySlots,
    onProduceClick,
  };
};
